/** Admin-defined custom steps injected into terminal flows (Phase 7). */
import { TERMINAL_FLOWS, findTerminalFlow, listCatalogSteps } from './terminal-flow-catalog.js';
import { buildHandlerStep, HANDLER_TEMPLATES } from './terminal-flow-handlers.js';

const MAX_CUSTOM_STEPS_PER_FLOW = 8;
const STEP_KEY_RE = /^custom_[a-z0-9_]+$/;

let customStepsByFlow = {};

export function setTerminalFlowCustomSteps(obj) {
  customStepsByFlow = obj && typeof obj === 'object' ? obj : {};
}

export function getTerminalFlowCustomSteps() {
  return customStepsByFlow;
}

export function getCustomStepsForFlow(flowId) {
  const list = customStepsByFlow[flowId];
  if (!Array.isArray(list)) return [];
  return list
    .filter((s) => s.enabled !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export function getAllCustomStepsForFlow(flowId) {
  const list = customStepsByFlow[flowId];
  return Array.isArray(list) ? [...list] : [];
}

export function findCustomStepById(flowId, id) {
  return getAllCustomStepsForFlow(flowId).find((s) => s.id === id) || null;
}

export function findCustomStepByKey(flowId, stepKey) {
  return getCustomStepsForFlow(flowId).find((s) => s.stepKey === stepKey) || null;
}

/** Valid anchor keys for custom step placement (built-in catalog + other custom steps). */
export function getAnchorKeysForFlow(flowId, excludeStepId = null) {
  const flow = findTerminalFlow(flowId);
  if (!flow) return new Set(['__start__', '__end__']);
  const builtIn = listCatalogSteps(flow).filter((s) => !s.custom).map((s) => s.key);
  const custom = getAllCustomStepsForFlow(flowId)
    .filter((s) => s.id !== excludeStepId)
    .map((s) => s.stepKey);
  return new Set([...builtIn, ...custom, '__start__', '__end__']);
}

export function validateCustomStepConfig(config, flowId, options = {}) {
  if (!config || typeof config !== 'object') return 'Invalid step config.';
  if (!flowId) return 'Pick a flow.';
  if (!findTerminalFlow(flowId)) return 'Unknown flow.';
  if (!HANDLER_TEMPLATES[config.handler]) return 'Pick a handler template.';
  const stepKey = String(config.stepKey || '').trim();
  if (!STEP_KEY_RE.test(stepKey)) return 'Step key must start with custom_ and use lowercase letters, numbers, underscores.';
  if (!String(config.ask || '').trim()) return 'Question text is required.';
  if (config.handler === 'numbered_choice') {
    const choices = String(config.choices || '').split('\n').map((s) => s.trim()).filter(Boolean);
    if (choices.length < 2) return 'Numbered choice needs at least 2 choices (one per line).';
  }
  const afterStep = String(config.afterStep || '__end__').trim();
  const anchors = getAnchorKeysForFlow(flowId, options.excludeStepId || config.id || null);
  if (!anchors.has(afterStep)) {
    return `Anchor step "${afterStep}" not found — it may have been removed from the catalog.`;
  }
  const siblings = getAllCustomStepsForFlow(flowId).filter((s) => s.id !== (options.excludeStepId || config.id));
  if (siblings.some((s) => s.stepKey === stepKey)) return `Step key "${stepKey}" already exists in this flow.`;
  return null;
}

export function sanitizeCustomStep(config, flowId) {
  return {
    id: config.id || `cs_${Date.now()}`,
    flowId,
    handler: config.handler,
    stepKey: String(config.stepKey).trim(),
    ask: String(config.ask).trim(),
    hint: String(config.hint || '').trim(),
    choices: String(config.choices || '').trim(),
    afterStep: String(config.afterStep || '__end__').trim(),
    sortOrder: Number.isFinite(Number(config.sortOrder)) ? Number(config.sortOrder) : 0,
    optional: config.optional != null ? !!config.optional : undefined,
    enabled: config.enabled !== false,
  };
}

export function catalogEntryFromCustom(config) {
  const handler = HANDLER_TEMPLATES[config.handler];
  return {
    key: config.stepKey,
    label: `[Custom] ${config.ask.slice(0, 40)}`,
    ask: config.ask,
    hint: config.hint || '',
    choices: config.choices || '',
    optional: config.optional != null ? config.optional : handler?.defaultOptional,
    writesTo: config.flowId?.includes('.stage.')
      ? `stage_data.custom.${config.stepKey}`
      : `extra.custom_steps.${config.stepKey}`,
    showsWhen: 'Admin custom step',
    impact: `Handler: ${handler?.label || config.handler}`,
    pattern: false,
    editable: ['ask', 'hint', 'choices'],
    custom: true,
    locked: false,
    behaviorEditable: true,
  };
}

export function listCustomCatalogSteps(flowId) {
  return getCustomStepsForFlow(flowId).map(catalogEntryFromCustom);
}

/** Insert custom handler steps after an anchor key in a built step array. */
export function injectCustomSteps(flowId, steps, ctx = {}) {
  const configs = getCustomStepsForFlow(flowId);
  if (!configs.length || !Array.isArray(steps)) return steps;

  const built = configs
    .map((cfg) => buildHandlerStep(cfg, ctx))
    .filter(Boolean);

  if (!built.length) return steps;

  const byAnchor = new Map();
  built.forEach((step, i) => {
    const cfg = configs[i];
    const anchor = cfg.afterStep || '__end__';
    if (!byAnchor.has(anchor)) byAnchor.set(anchor, []);
    byAnchor.get(anchor).push(step);
  });

  const out = [];
  if (byAnchor.has('__start__')) out.push(...byAnchor.get('__start__'));
  steps.forEach((step) => {
    out.push(step);
    if (byAnchor.has(step.key)) out.push(...byAnchor.get(step.key));
  });
  if (byAnchor.has('__end__')) out.push(...byAnchor.get('__end__'));
  return out;
}

export function assertCustomStepLimit(flowId, nextList) {
  if ((nextList?.length || 0) > MAX_CUSTOM_STEPS_PER_FLOW) {
    return `Maximum ${MAX_CUSTOM_STEPS_PER_FLOW} custom steps per flow.`;
  }
  return null;
}

const STALE_CUSTOM_KINDS = new Set(['unknown_flow', 'orphan_anchor', 'duplicate_key', 'invalid_handler', 'bad_step_key']);

/** Custom steps with broken anchors, unknown flows, or duplicate keys. */
export function findStaleCustomSteps(customStepsMap = {}) {
  const issues = [];
  const flowIds = new Set(TERMINAL_FLOWS.map((f) => f.id));

  for (const [flowId, list] of Object.entries(customStepsMap)) {
    if (!Array.isArray(list)) continue;
    if (!flowIds.has(flowId)) {
      list.forEach((s) => {
        issues.push({
          kind: 'unknown_flow',
          flowId,
          stepId: s.id,
          stepKey: s.stepKey,
          message: `Custom step "${s.stepKey}" references unknown flow ${flowId}`,
        });
      });
      continue;
    }

    const seenKeys = new Set();
    list.forEach((s) => {
      if (!STEP_KEY_RE.test(String(s.stepKey || ''))) {
        issues.push({
          kind: 'bad_step_key',
          flowId,
          stepId: s.id,
          stepKey: s.stepKey,
          message: `${flowId}: invalid custom step key "${s.stepKey}"`,
        });
      }
      if (!HANDLER_TEMPLATES[s.handler]) {
        issues.push({
          kind: 'invalid_handler',
          flowId,
          stepId: s.id,
          stepKey: s.stepKey,
          message: `${flowId}.${s.stepKey}: unknown handler "${s.handler}"`,
        });
      }
      if (seenKeys.has(s.stepKey)) {
        issues.push({
          kind: 'duplicate_key',
          flowId,
          stepId: s.id,
          stepKey: s.stepKey,
          message: `${flowId}: duplicate custom step key "${s.stepKey}"`,
        });
      }
      seenKeys.add(s.stepKey);

      const anchor = String(s.afterStep || '__end__').trim();
      const anchors = getAnchorKeysForFlow(flowId, s.id);
      if (!anchors.has(anchor)) {
        issues.push({
          kind: 'orphan_anchor',
          flowId,
          stepId: s.id,
          stepKey: s.stepKey,
          message: `${flowId}.${s.stepKey}: anchor "${anchor}" no longer exists`,
        });
      }
    });
  }
  return issues.filter((i) => STALE_CUSTOM_KINDS.has(i.kind));
}

export function removeStaleCustomSteps(customStepsMap = {}) {
  const stale = findStaleCustomSteps(customStepsMap);
  const removeIds = new Set(stale.map((i) => `${i.flowId}:${i.stepId}`));
  const next = {};
  let removed = 0;

  for (const [flowId, list] of Object.entries(customStepsMap)) {
    if (!Array.isArray(list)) continue;
    const kept = [];
    const seenKeys = new Set();
    list.forEach((s) => {
      const staleHit = removeIds.has(`${flowId}:${s.id}`)
        || !TERMINAL_FLOWS.some((f) => f.id === flowId)
        || !HANDLER_TEMPLATES[s.handler]
        || !STEP_KEY_RE.test(String(s.stepKey || ''))
        || seenKeys.has(s.stepKey);
      if (staleHit) {
        removed += 1;
        return;
      }
      const anchor = String(s.afterStep || '__end__').trim();
      if (!getAnchorKeysForFlow(flowId, s.id).has(anchor)) {
        removed += 1;
        return;
      }
      seenKeys.add(s.stepKey);
      kept.push(s);
    });
    if (kept.length) next[flowId] = kept;
  }

  return { next, removed };
}

/** Move custom step earlier (−1) or later (+1) within the same anchor, or shift anchor when alone. */
export function moveCustomStepInFlow(customStepsMap = {}, flowId, stepId, delta) {
  const flow = findTerminalFlow(flowId);
  if (!flow || !delta) return { next: customStepsMap, moved: false };
  const builtIn = listCatalogSteps(flow).filter((s) => !s.pattern && !s.custom).map((s) => s.key);
  const sequence = ['__start__', ...builtIn];
  const list = [...(customStepsMap[flowId] || [])];
  const step = list.find((s) => s.id === stepId);
  if (!step) return { next: customStepsMap, moved: false };

  const anchor = step.afterStep || '__end__';
  const siblings = list
    .filter((s) => (s.afterStep || '__end__') === anchor)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.stepKey).localeCompare(String(b.stepKey)));

  if (siblings.length > 1) {
    const idx = siblings.findIndex((s) => s.id === stepId);
    const newIdx = idx + delta;
    if (newIdx >= 0 && newIdx < siblings.length) {
      const reordered = [...siblings];
      const [item] = reordered.splice(idx, 1);
      reordered.splice(newIdx, 0, item);
      const orderMap = Object.fromEntries(reordered.map((s, i) => [s.id, i]));
      const nextList = list.map((s) => (
        orderMap[s.id] != null ? { ...s, sortOrder: orderMap[s.id] } : s
      ));
      const next = { ...customStepsMap };
      if (nextList.length) next[flowId] = nextList;
      else delete next[flowId];
      return { next, moved: true, afterStep: anchor, reordered: true };
    }
  }

  const currentAnchor = step.afterStep || '__end__';
  let anchorIdx = sequence.indexOf(currentAnchor);
  if (currentAnchor === '__end__') anchorIdx = sequence.length;
  if (currentAnchor === '__start__') anchorIdx = 0;

  const newIdx = anchorIdx + delta;
  if (newIdx < 0 || newIdx > sequence.length) return { next: customStepsMap, moved: false };

  const newAnchor = newIdx >= sequence.length ? '__end__' : sequence[newIdx];
  const nextList = list.map((s) => (s.id === stepId ? { ...s, afterStep: newAnchor } : s));
  const next = { ...customStepsMap };
  if (nextList.length) next[flowId] = nextList;
  else delete next[flowId];
  return { next, moved: true, afterStep: newAnchor };
}

export function countAllStaleFlowConfig(overrides = {}, customStepsMap = {}) {
  return findStaleOverrides(overrides).length + findStaleCustomSteps(customStepsMap).length;
}
